import type { BookingStatus } from '@navio/nsdk';
import type { NoddiIconName } from 'noddi-ui-common';
import { type FilterControl, filterUrlState } from 'noddi-ui-web';
import { CANCELLED_BOOKING_STATUSES, COMPLETED_BOOKING_STATUSES, UPCOMING_BOOKING_STATUSES } from 'noddi-util';
import { type BookingListFilterInput, bookingFilterUnit } from './bookingFilterSet';
import { BOOKING_STATUS_FILTER_OPTIONS } from './useBookingStatusFilter';
import { UNABLE_TO_COMPLETE_STATUS_FILTER_OPTIONS } from './useUnableToCompleteStatusFilter';

type Lifecycle = 'upcoming' | 'completed' | 'cancelled';

const LIFECYCLE_STATUSES: Record<Lifecycle, BookingStatus[]> = {
  upcoming: UPCOMING_BOOKING_STATUSES,
  completed: COMPLETED_BOOKING_STATUSES,
  cancelled: CANCELLED_BOOKING_STATUSES
};

const STATUS_TEXT = {
  title: 'Booking status',
  description:
    'Payment requested: created on the customer’s behalf, held until they pay. Confirmed: booking stands. Assigned: booking is assigned to a route. Completed: booking is done. Cancelled: booking has been cancelled.'
};

/** The status facet. Its tray names each lifecycle as the status set it stands for, on the same param. */
const statusSpec = bookingFilterUnit.multiSelect('statusIds', {
  ...STATUS_TEXT,
  options: BOOKING_STATUS_FILTER_OPTIONS,
  exclude: true,
  quickFilter: {
    label: 'Booking lifecycle',
    options: [
      { value: 'all', label: 'All' },
      { value: 'upcoming', label: 'Upcoming', values: LIFECYCLE_STATUSES.upcoming },
      { value: 'completed', label: 'Completed', values: LIFECYCLE_STATUSES.completed },
      { value: 'cancelled', label: 'Cancelled', values: LIFECYCLE_STATUSES.cancelled }
    ]
  }
});

const STATUS_URL_KEY = filterUrlState.urlKey('statusIds');
const EXCLUDED_STATUS_URL_KEY = filterUrlState.urlKey('excludedStatusIds');

/**
 * Lifecycle (sidebar and tray) and the status facet, all on `statusIds`, so one unit. `baseExcludedStatusIds`
 * are statuses the list leaves out unless picked: sent with the facet's own exclusions, never shown as the user's.
 * A builder cannot hold the lifecycle control (a second control on the facet's param) or the locked exclusion.
 */
export const bookingStatusFilter = ({
  baseExcludedStatusIds = [],
  withQuickFilter
}: {
  baseExcludedStatusIds?: BookingStatus[];
  /** Off for lists whose tray filters something else (adapter lists: direction). */
  withQuickFilter: boolean;
}) =>
  bookingFilterUnit.compose(`bookingStatus:${baseExcludedStatusIds.join(',')}`, [statusSpec], (values, setValues) => {
    const status = statusSpec.resolve(values, setValues);
    const statusIds = status.apiInput.statusIds ?? [];
    const excluded = status.apiInput.excludedStatusIds ?? [];
    // A lifecycle replaces the whole status scope, exclusions included.
    const setLifecycle = (lifecycle: Lifecycle | null) =>
      setValues(() => ({
        [STATUS_URL_KEY]: lifecycle ? LIFECYCLE_STATUSES[lifecycle] : undefined,
        [EXCLUDED_STATUS_URL_KEY]: []
      }));
    const lifecycle: FilterControl = {
      id: 'bookingLifecycle',
      type: 'single-select',
      title: 'Booking',
      description: 'Quick filter for upcoming (confirmed + assigned), completed, or cancelled bookings.',
      options: LIFECYCLE_OPTIONS,
      canExclude: false,
      // An excluded status is a scope no lifecycle names, so it highlights nothing.
      value: { include: excluded.length > 0 ? null : lifecycleOf(statusIds), exclude: [] },
      onChange: (next) => setLifecycle(toLifecycle(next.include)),
      reset: () => setLifecycle(null),
      isInitial: values[STATUS_URL_KEY] === undefined && excluded.length === 0
    };
    // A picked status overrides the list's default exclusion: sent both ways, it would match nothing.
    const baseExcluded = baseExcludedStatusIds.filter((id) => !excluded.includes(id) && !statusIds.includes(id));
    return {
      controls: {
        controls: [lifecycle, ...status.controls],
        quickFilter: withQuickFilter ? status.quickFilter : undefined
      },
      params: { statusIds: status.apiInput.statusIds, excludedStatusIds: [...baseExcluded, ...excluded] }
    };
  });

/** Bulk reschedule only moves confirmed and assigned bookings: the status is shown, not editable. */
export const upcomingStatusOnlyFilter = bookingFilterUnit.compose<Pick<BookingListFilterInput, 'statusIds'>>(
  'upcomingStatusOnly',
  {},
  () => ({
    controls: {
      ...STATUS_TEXT,
      id: 'statusIds',
      type: 'multi-select',
      description: 'Only confirmed and assigned bookings can be rescheduled.',
      options: BOOKING_STATUS_FILTER_OPTIONS,
      canExclude: false,
      value: { include: UPCOMING_BOOKING_STATUSES, exclude: [] },
      onChange: () => undefined,
      reset: () => undefined,
      isInitial: true
    },
    params: { statusIds: UPCOMING_BOOKING_STATUSES }
  })
);

/** For lists whose status is locked (bulk reschedule): picking here must not touch the list behind it. */
export const unableToCompleteStatusFilter = bookingFilterUnit.multiSelect('unableToCompleteStatus', {
  title: 'Unable to complete status',
  description:
    'Whether the worker could not finish the job. Partially means some tasks were completed, fully means no tasks could be completed.',
  options: UNABLE_TO_COMPLETE_STATUS_FILTER_OPTIONS,
  exclude: true
});

const UNABLE_TO_COMPLETE_URL_KEY = filterUrlState.urlKey('unableToCompleteStatus');

/**
 * An unable-to-complete status is never a live booking status, so picking one clears the status facet in the
 * same write. A compose because the control writes another filter's URL key.
 */
export const unableToCompleteClearingStatusFilter = bookingFilterUnit.compose(
  'unableToCompleteClearingStatus',
  [unableToCompleteStatusFilter, statusSpec],
  (values, setValues) => {
    const unit = unableToCompleteStatusFilter.resolve(values, (changes) =>
      setValues((latest) => {
        const next = changes(latest);
        const picked = next[UNABLE_TO_COMPLETE_URL_KEY];
        return Array.isArray(picked) && picked.length > 0 ? { ...next, [STATUS_URL_KEY]: undefined } : next;
      })
    );
    return { controls: unit.controls, params: unit.apiInput };
  }
);

const LIFECYCLE_OPTIONS = [
  { label: 'Upcoming', value: 'upcoming', icon: 'CalendarClock' },
  { label: 'Completed', value: 'completed', icon: 'CheckCircle' },
  { label: 'Cancelled', value: 'cancelled', icon: 'XCircle' }
] satisfies { label: string; value: Lifecycle; icon: NoddiIconName }[];

const isLifecycle = (value: unknown): value is Lifecycle => typeof value === 'string' && value in LIFECYCLE_STATUSES;

const toLifecycle = (value: unknown) => (isLifecycle(value) ? value : null);

const sameStatuses = (a: BookingStatus[], b: BookingStatus[]) =>
  a.length === b.length && a.every((value) => b.includes(value));

const lifecycleOf = (statusIds: BookingStatus[]): Lifecycle | null =>
  LIFECYCLE_OPTIONS.find((option) => sameStatuses(statusIds, LIFECYCLE_STATUSES[option.value]))?.value ?? null;
